import { ICredentials, IRefreshTokenData, ITokens, LocalTokens } from '../../types/auth';
import { ErrorCode } from '../../types/errorCodes';
import { API } from '../api';
import { request } from '../request';
import { Api } from '../ApiEndpoints/ApiEndpoints';
import { authChannel } from '../../index';
import { ADMIN_TOKEN_UPDATED } from '../../config/data';
import { ClientId } from '../../config/tokens';
import {
  getAuthenticationTokenForAdmin,
  getAuthenticationTokenForSelfCustomer,
  getRefreshTokenForAdmin,
  getRefreshTokenForSelfCustomer,
  getSuTokens,
  removeAuthenticationTokenForAdmin,
  removeAuthenticationTokenForSelfCustomer,
  removeRefreshTokenForAdmin,
  removeRefreshTokenForSelfCustomer,
  removeSuTokens,
  setAuthenticationTokenForAdmin,
  setAuthenticationTokenForSelfCustomer,
  setRefreshTokenForAdmin,
  setRefreshTokenForSelfCustomer,
  setSuTokens,
} from '../helper';

class AuthService {
  getLocalToken(): string {
    return getAuthenticationTokenForAdmin() || getAuthenticationTokenForSelfCustomer() || '';
  }

  getRefreshToken(): string {
    return getRefreshTokenForAdmin() || getRefreshTokenForSelfCustomer() || '';
  }

  setTokenForRefresh({ accessToken, refreshToken }: ITokens) {
    if (getAuthenticationTokenForAdmin()) {
      setAuthenticationTokenForAdmin(accessToken);
      setRefreshTokenForAdmin(refreshToken);
    }
    if (getAuthenticationTokenForSelfCustomer()) {
      setAuthenticationTokenForSelfCustomer(accessToken);
      setRefreshTokenForSelfCustomer(refreshToken);
    }
  }

  setTokens({ accessToken, refreshToken }: ITokens): void {
    // set token for admin
    setAuthenticationTokenForAdmin(accessToken);
    setRefreshTokenForAdmin(refreshToken);

    // remove token for self-customer, because it is an admin login
    removeAuthenticationTokenForSelfCustomer();
    removeRefreshTokenForSelfCustomer();

    // send a message for reload all pages with opened booking/admin-panel
    authChannel.postMessage({
      type: ADMIN_TOKEN_UPDATED,
    });
  }

  setDealershipTokens({ accessToken, refreshToken }: ITokens) {
    const tokens: ITokens = {
      accessToken: this.getLocalToken(),
      refreshToken: this.getRefreshToken(),
    };
    setSuTokens(JSON.stringify(tokens));
    setAuthenticationTokenForAdmin(accessToken);
    setRefreshTokenForAdmin(refreshToken);
  }

  isAuthenticated(): boolean {
    return !!getAuthenticationTokenForAdmin();
  }

  /**
   * Makes sure an anonymous (Booking Flow Anonymous) token is available before calling an endpoint
   * that is not [AllowAnonymous]. Recipients of outbound SMS/email links are not logged in, so the
   * self-customer token has to be acquired up front. Unlike the interceptor in request.ts this does
   * not reload the page - the caller awaits it and then issues its own request.
   */
  async ensureAnonymousToken(): Promise<void> {
    if (!this.getLocalToken()) {
      const resp = await Api.call<ITokens>(Api.endpoints.Authentications.Anonymous, {
        data: { ClientId },
      });
      setAuthenticationTokenForSelfCustomer(resp.data.accessToken);
      setRefreshTokenForSelfCustomer(resp.data.refreshToken);
    }
    this.syncRequestAuthWithLocalStorage();
  }

  syncRequestAuthWithLocalStorage(): void {
    const token = this.getLocalToken();
    if (token) {
      request.defaults.headers['Authorization'] = `Bearer ${token}`;
      request.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete request.defaults.headers['Authorization'];
      delete request.defaults.headers.common['Authorization'];
    }
  }

  async refresh() {
    try {
      const data: IRefreshTokenData = { token: this.getRefreshToken() };
      const resp = await Api.call<ITokens>(Api.endpoints.Authentications.Refresh, { data });
      this.setTokenForRefresh(resp.data);
      this.syncRequestAuthWithLocalStorage();
      return resp.data.accessToken;
    } catch (e) {
      // for refresh dead token
      removeAuthenticationTokenForSelfCustomer();
      removeRefreshTokenForSelfCustomer();
      removeAuthenticationTokenForAdmin();
      removeRefreshTokenForAdmin();

      // send a message for reload all pages with opened booking/admin-panel
      authChannel.postMessage({
        type: ADMIN_TOKEN_UPDATED,
      });

      console.error(e);

      // reload current page
      window.location.reload();
    }
  }

  async dealershipLogin(dealershipId: number) {
    try {
      const response = await API.authentication.dealership(dealershipId);
      this.setDealershipTokens(response.data);

      // send a message for reload all pages with opened booking/admin-panel
      authChannel.postMessage({
        type: ADMIN_TOKEN_UPDATED,
      });
      this.syncRequestAuthWithLocalStorage();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      if (e?.response?.data?.errorCode === ErrorCode.InvalidPermission) {
        window.location.reload();
      }
      console.error(e);
    }
  }

  async login(data: ICredentials) {
    // TODO: IT IS TEMPORARY FIX, FOR CLEAR ALL SESSION STORAGE REGARDING OUR PAST LOGIC, delete it after 15.07.2025
    if (sessionStorage.getItem(LocalTokens.authToken)) {
      sessionStorage.removeItem(LocalTokens.authToken);
    }

    if (sessionStorage.getItem(LocalTokens.refreshToken)) {
      sessionStorage.removeItem(LocalTokens.refreshToken);
    }

    const resp = await Api.call<ITokens>(Api.endpoints.Authentications.Request, { data });
    this.setTokens(resp.data);
    this.syncRequestAuthWithLocalStorage();
  }

  logout(isSuperUser: boolean): void {
    // remove admin tokens
    removeAuthenticationTokenForAdmin();
    removeRefreshTokenForAdmin();

    const suTokens = getSuTokens();

    if (suTokens) {
      removeSuTokens();
      if (isSuperUser) {
        this.setTokens(JSON.parse(suTokens) as ITokens);
      }
      authChannel.postMessage({
        type: ADMIN_TOKEN_UPDATED,
      });
    } else {
      authChannel.postMessage({
        type: ADMIN_TOKEN_UPDATED,
      });
    }
    this.syncRequestAuthWithLocalStorage();
  }
}

export const authService = new AuthService();
